import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Container,
  Paper,
  InputBase,
  Link,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { RequestUnlockOTP, VerifyUnlockOTP } from "../../config";

const theme = createTheme({
  palette: {
    primary: { main: "#4caf50" },
  },
});

export default function OTPVerification({ onVerifySuccess }) {
  const [otp, setOtp] = React.useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [keepSignedIn, setKeepSignedIn] = React.useState(true);
  const inputRefs = React.useRef([]);
  const navigate = useNavigate();

  const getEmailFromToken = () => {
    try {
      const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
      return decodedToken?.email || decodedToken?.Email || null;
    } catch {
      return null;
    }
  };

  const email = getEmailFromToken();

  // ✅ Automatically request OTP on first page load
  React.useEffect(() => {
    if (!email) {
      Swal.fire({
        icon: "error",
        title: "Session Error",
        text: "User session not found. Please log in again.",
      }).then(() => navigate("/login"));
      return;
    }

    // Automatically send OTP once when user arrives at this page
    const autoSendOtp = async () => {
      try {
        setResendLoading(true);
        const response = await fetch(RequestUnlockOTP.uri, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        if (response.ok) {
          setOtp(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: "Unable to send OTP automatically. Try resending manually.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Network Error",
          text: "Could not send new code. Check your connection.",
        });
      } finally {
        setResendLoading(false);
      }
    };

    autoSendOtp(); // Trigger once on page load
  }, [email, navigate]);

  // ✅ Manual resend function (still available to user)
  const handleResend = async () => {
    setResendLoading(true);
    try {
      const response = await fetch(RequestUnlockOTP.uri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Sent!",
          text: "A new code has been sent to your email.",
        });
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Could not send new code.",
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Could not send new code.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (paste.length > 0) {
      const newOtp = paste
        .split("")
        .concat(Array(6).fill("").slice(paste.length));
      setOtp(newOtp.slice(0, 6));
      inputRefs.current[Math.min(paste.length - 1, 5)].focus();
    }
    e.preventDefault();
  };

  const handleSubmit = async () => {
    const unlockOTP = otp.join("");
    const decodedTokenString = localStorage.getItem("decodedToken");

    if (unlockOTP.length !== 6) {
      Swal.fire({
        icon: "warning",
        title: "Invalid OTP",
        text: "Please enter all 6 digits.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(VerifyUnlockOTP.uri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, unlockOTP }),
      });

      if (response.ok) {
        let decodedToken = null;
        try {
          decodedToken = JSON.parse(decodedTokenString);
        } catch {
          Swal.fire({
            icon: "error",
            title: "Session Expired",
            text: "Please log in again.",
          });
          navigate("/login");
          return;
        }

        const isCompany = parseInt(decodedToken?.RoleGroupId == 2);
        const isRegion = parseInt(decodedToken?.RoleGroupId == 3);
        const isAdmin = parseInt(decodedToken?.RoleGroupId == 1);

        onVerifySuccess && onVerifySuccess();

        if (isCompany || isRegion || isAdmin) {
          navigate("/MCB-SWITCH");
        } else {
          navigate("/super-admin-dashboard");
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: "Invalid or expired OTP.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "Something went wrong. Try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundSize: "contain",
          display: "flex",
          alignItems: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.92)",
            zIndex: 1,
          },
        }}
      >
        <Container
          component="main"
          maxWidth="sm"
          sx={{ position: "relative", zIndex: 2 }}
        >
          <Paper
            elevation={10}
            sx={{
              p: 4,
              borderRadius: 3,
              backdropFilter: "blur(10px)",
              background: "rgba(255, 255, 255, 0.95)",
            }}
          >
            <Typography
              component="h1"
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: "#4caf50", fontWeight: "bold" }}
            >
              Confirm Your Identity
            </Typography>

            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              paragraph
            >
              A verification code was sent to:{" "}
              <strong>{email || "loading..."}</strong>.
            </Typography>

            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, my: 3 }}
            >
              {otp.map((digit, index) => (
                <InputBase
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                    },
                  }}
                  sx={{
                    width: 50,
                    height: 50,
                    border: "2px solid",
                    borderColor: digit ? "primary.main" : "#e0e0e0",
                    borderRadius: 2,
                    bgcolor: digit ? "primary.50" : "grey.50",
                    transition: "all 0.2s",
                    "&:focus": {
                      outline: "none",
                      borderColor: "primary.main",
                      boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.3)",
                    },
                  }}
                />
              ))}
            </Box>

            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                  color="primary"
                />
              }
              label="Keep me signed in"
              sx={{ mt: 1, mb: 2 }}
            /> */}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || otp.join("").length !== 6}
              sx={{
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1.1rem",
                bgcolor: "#4caf50",
                "&:hover": { bgcolor: "#43a047" },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "VERIFY"
              )}
            </Button>

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleResend}
                disabled={resendLoading}
                sx={{
                  color: "#4caf50",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {resendLoading ? "Sending..." : "Send New Code"}
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
